import {
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { formatDateKey } from '../format-date-key';
import { isBetween } from '../lib/date-fns';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { getScheduleEndDate } from './get-schedule-end-date';
import {
  getNextScheduleTimeframe,
  getScheduleTimeframesByDateRange,
} from './get-schedule-timeframe-by-date';
import { memoizeByScheduleAndVariant } from './memoize-by-schedule';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  today: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

type FrequencyEntryIndexOptions = {
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

type FrequencyEntryIndex = {
  skippedDates: Set<string>;
  completedDates: Set<string>;
  allCompletedDates: Set<string>;
  lastEntryDate?: Timestamp;
  completedValues: Map<string, number>;
};

// Range-independent: nothing here reads startDate/endDate/today, so one pass per schedule+flags
// serves every date window the caller asks for. Returned collections are shared — never mutate them.
const getFrequencyEntryIndex = memoizeByScheduleAndVariant<
  FrequencyEntryIndexOptions,
  FrequencyEntryIndex
>(
  ({ uncompletedOnly, sinceLastEntryOnly }) =>
    `${uncompletedOnly ? 1 : 0}:${sinceLastEntryOnly ? 1 : 0}`,
  (schedule, { uncompletedOnly, sinceLastEntryOnly }) =>
    schedule.entries.reduce<FrequencyEntryIndex>(
      (acc, entry) => {
        // get the skipped dates to exclude them from the plannedOccurrences
        if (
          entry.state === ScheduleEntryState.Skip ||
          entry.state === ScheduleEntryState.Failure
        ) {
          acc.skippedDates.add(formatDateKey(entry.date));
        }

        // get the completed dates to exclude them from the plannedOccurrences
        if (entry.state === ScheduleEntryState.Complete && !!entry.value) {
          const dateString = formatDateKey(entry.date);
          acc.allCompletedDates.add(dateString);
          acc.completedValues.set(
            dateString,
            (acc.completedValues.get(dateString) ?? 0) + entry.value,
          );

          if (uncompletedOnly) {
            acc.completedDates.add(dateString);
          }
        }

        if (
          sinceLastEntryOnly &&
          ((entry.state === ScheduleEntryState.Complete && !!entry.value) ||
            entry.state !== ScheduleEntryState.Complete)
        ) {
          acc.lastEntryDate =
            !acc.lastEntryDate ||
            acc.lastEntryDate.getTime() < entry.date.getTime()
              ? entry.date
              : acc.lastEntryDate;
        }

        return acc;
      },
      {
        skippedDates: new Set(),
        completedDates: new Set(),
        allCompletedDates: new Set(),
        lastEntryDate: undefined,
        completedValues: new Map(),
      },
    ),
);

export const getFrequencyPlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    today,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleEnd = getScheduleEndDate(schedule);

  const _start = new Date(
    Math.max(
      // actual start (not nominal startDate) keeps the pre-start timeframes below reachable
      startDate.getTime(),
      getActualScheduleStartDate(schedule).getTime(),
    ),
  );

  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // get the timeframes based on the start, end and frequency
  const count = schedule.frequency?.count ?? 0;

  const {
    skippedDates,
    completedDates,
    allCompletedDates,
    lastEntryDate,
    completedValues,
  } = getFrequencyEntryIndex(schedule, { uncompletedOnly, sinceLastEntryOnly });

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];
  // today's own frame can roll an occurrence into the next one, so it stays in range; nothing earlier is read.
  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: new Date(
      Math.max(
        scheduleStartDate.getTime(),
        Math.min(startOfDay(startDate).getTime(), startOfDay(today).getTime()),
      ),
    ),
    endDate: _end,
    weekStartsOn,
  });

  if (!preTimeframes.length) {
    // when there are no entries before the schedule startDate, the schedule startDate is the exact starting point
    duringTimeframes.forEach((timeframe) => {
      timeframe.startDate = new Date(
        Math.max(timeframe.startDate.getTime(), scheduleStartDate.getTime()),
      );
    });
  }
  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(date, timeframe.startDate, timeframe.endDate),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    const isWithinBounds = (date: Timestamp) =>
      (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
      // occurrences from before the start date are not wanted
      !isBefore(date, startDate) &&
      // occurrences after the end date are not wanted
      !isAfter(date, _end);

    const daysInFrame = eachDayOfInterval({
      start: timeframe.startDate,
      end: timeframe.endDate,
    });

    const completedDaysInFrame = daysInFrame.filter((date) =>
      allCompletedDates.has(formatDateKey(date)),
    );

    // completed entries surface on their own day even in planned timeframes
    const completedOccurrences = uncompletedOnly
      ? []
      : completedDaysInFrame
          .filter(
            (date) =>
              isWithinBounds(date) &&
              !plannedOccurrences.some((planned) =>
                isSameDay(planned.date, date),
              ),
          )
          .map((date) => ({
            id: uuidv4(),
            date: endOfDay(date),
            timeslot: schedule.timeslot ?? null,
          }));

    if (plannedOccurrences.length) {
      acc.push(
        ...plannedOccurrences.filter(
          ({ date }) =>
            !skippedDates.has(formatDateKey(date)) &&
            !completedDates.has(formatDateKey(date)) &&
            isWithinBounds(date),
        ),
        ...completedOccurrences,
      );

      return acc;
    }

    acc.push(...completedOccurrences);

    // if today is not in the current timeframe, there are no other occurrences to be added.
    if (!isBetween(today, timeframe.startDate, timeframe.endDate)) {
      return acc;
    }

    // add remaining occurrences on "Today" (or start/end of period)
    const completedValueInFrame = completedDaysInFrame.reduce((acc, date) => {
      const dateString = formatDateKey(date);
      return acc + (completedValues.get(dateString) ?? 0);
    }, 0);

    const remaining = Math.max(0, count - completedValueInFrame);

    if (!remaining) {
      const nextTimeframeDate = endOfDay(
        getNextScheduleTimeframe(schedule, today, weekStartsOn).startDate,
      );
      if (
        isBetween(today, timeframe.startDate, timeframe.endDate) &&
        !isAfter(nextTimeframeDate, _end)
      ) {
        acc.push({
          id: uuidv4(),
          date: nextTimeframeDate,
          timeslot: schedule.timeslot ?? null,
        });
      }

      return acc;
    }

    const todayKey = formatDateKey(today);
    const targetDay =
      skippedDates.has(todayKey) || allCompletedDates.has(todayKey)
        ? addDays(today, 1)
        : today;

    const targetDate = endOfDay(targetDay);

    if (isWithinBounds(targetDate)) {
      acc.push({
        id: uuidv4(),
        date: targetDate,
        timeslot: schedule.timeslot ?? null,
      });
    }

    return acc;
  }, []);
};
